import { useState, useEffect, useCallback } from 'react';
import { userService } from '../services/UserService';
import useAlert from '../../../shared/hooks/useAlert';
import { usePagination } from '../../../shared/hooks/usePagination';

export const useUser = () => {
  const { alert, showAlert } = useAlert();

  // Gunakan hook pagination dari utils
  const {
    // State dari usePagination
    pagination,
    data,
    meta,
    links,
    loading,
    error,

    // Setters dari usePagination
    setData,
    setLoading,
    setError,
    setMeta,
    setLinks,

    // Actions dari usePagination
    updateFromResponse,
    updatePagination,
    goToPage,
    changeLimit,
    changeSort,
    toggleSortOrder,
    search,
    resetPagination,

    // Utilities dari usePagination
    buildQueryParams,
    getQueryObject: getBaseQueryObject,
    getSortInfo,

    // Computed values dari usePagination
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    currentSortField,
    currentSortOrder,
  } = usePagination();

  // State untuk filters
  const [filters, setFilters] = useState({
    role: 'USER',
  });

  // Set user dari data
  const user = data;

  // Build query object dengan filters
  const getQueryObject = useCallback(() => {
    const baseQuery = getBaseQueryObject();

    // Add filter parameters if not 'ALL'
    const queryWithFilters = {
      ...baseQuery,
      ...(filters.role !== 'ALL' && { role: filters.role }),
    };

    return queryWithFilters;
  }, [getBaseQueryObject, filters]);

  // Build query params untuk API call
  const buildQueryParamsWithFilters = useCallback(() => {
    const queryObject = getQueryObject();
    const params = new URLSearchParams();

    Object.entries(queryObject).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    return params.toString();
  }, [getQueryObject]);

  // Fetch user dengan pagination dan filters
  const fetchUser = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryObject = getQueryObject();
      const queryParams = buildQueryParamsWithFilters();

      const result = await userService.getPaginate(queryParams);

      if (result.success) {
        updateFromResponse(result.data);
      } else {
        console.error('❌ API Error:', result.error);
        setError(result.error);
        showAlert(result.error, 'danger');
      }
    } catch (err) {
      console.error('🚨 Network Error:', err);
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
    } finally {
      setLoading(false);
    }
  }, [buildQueryParamsWithFilters, setError, setLoading, showAlert, updateFromResponse, getQueryObject, filters]);

  // Update filter functions
  const updateRoleFilter = useCallback((role) => {
    setFilters(prev => ({ ...prev, role }));
    goToPage(1); // Reset to page 1 when filter changes
  }, [goToPage]);

  const updateTypeFilter = useCallback((type) => {
    setFilters(prev => ({ ...prev, type }));
    goToPage(1); // Reset to page 1 when filter changes
  }, [goToPage]);

  const clearFilters = useCallback(() => {
    setFilters({
      role: 'ALL',
    });
    goToPage(1);
  }, [goToPage]);

  // User CRUD operations
  const addUser = useCallback(async (userData) => {
    setLoading(true);
    try {
      const result = await userService.create(userData);
      if (result.success) {
        showAlert('User created successfully!', 'success');
        fetchUser(); // Refresh data
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
        return {
          success: false,
          error: result.error,
          validationErrors: result.validationErrors
        };
      }
    } catch (err) {
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [fetchUser, setError, setLoading, showAlert]);

  const fetchUserById = useCallback(async (id) => {
    try {
      const result = await userService.getById(id);
      if (result.success) {
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
      return { success: false, error: errorMessage };
    }
  }, [setError, showAlert]);

  const editUser = useCallback(async (id, updatedUser) => {
    setLoading(true);
    try {
      const result = await userService.update(id, updatedUser);
      if (result.success) {
        // Update item di state
        setData(prevData =>
          prevData.map(p => (p.id === id ? result.data : p))
        );
        showAlert('User updated successfully!', 'success');
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
        return {
          success: false,
          error: result.error,
          validationErrors: result.validationErrors
        };
      }
    } catch (err) {
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [setData, setError, setLoading, showAlert]);

  const removeUser = useCallback(async (id) => {
    try {
      const result = await userService.delete(id);
      if (result.success) {
        // Remove item dari state
        setData(prevData => prevData.filter(p => p.id !== id));
        showAlert('User deleted successfully!', 'success');
        return { success: true };
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
      return { success: false, error: errorMessage };
    }
  }, [setData, setError, showAlert]);

  const updateOnActive = useCallback(async (id, updatedStatus) => {
    try {
      const result = await userService.updateActive(id, updatedStatus);
      if (result.success) {
        // Update item di state
        setData(prevData =>
          prevData.map(p => (p.id === id ? result.data : p))
        );
        showAlert('Status updated successfully!', 'success');
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
      return { success: false, error: errorMessage };
    }
  }, [setData, setError, showAlert]);

  const syncDataDokter = useCallback(async () => {
    setLoading(true);
    try {
      const result = await userService.syncDataDokter();
      if (result.success) {
        showAlert('Data dokter synchronized successfully!', 'success');
        fetchUser(); // Refresh data
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [fetchUser, setError, setLoading, showAlert]);

  // Fetch data saat pagination atau filters berubah
  useEffect(() => {
    fetchUser();
  }, [
    pagination.page,
    pagination.limit,
    pagination.sortBy,
    pagination.search,
    filters.role,
  ]);

  return {
    // Data state
    user,
    loading,
    error,
    alert,

    // Pagination state
    pagination,
    meta,
    links,

    // Filter state
    filters,

    // Pagination actions
    fetchUser,
    goToPage,
    changeLimit,
    changeSort,
    toggleSortOrder,
    search,
    resetPagination,

    // Filter actions
    updateRoleFilter,
    clearFilters,

    // Pagination info
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    currentSortField,
    currentSortOrder,

    // User CRUD operations
    addUser,
    fetchUserById,
    editUser,
    removeUser,
    updateOnActive,
    syncDataDokter,

    // Utilities
    buildQueryParams: buildQueryParamsWithFilters,
    getQueryObject,
    getSortInfo,
  };
};